import type { QualitySetting } from '../core/Renderer';
import type { Lang } from '../i18n';
import { SHOP, STAGES } from './content.ts';
import { cleanGear, emptyGear, type GearState } from './gear.ts';
import { MAX_STORM_TIER } from './rules.ts';

export interface Settings {
  graphicsMode: QualitySetting; // was `graphics` (and `quality` before): old values are deliberately ignored, so everyone moves to the Auto default once
  sfx: number;
  music: number;
  shake: boolean;
  numbers: boolean;
  lang: Lang;
  joystick: 'floating' | 'fixed';
  leftHanded: boolean;
  haptics: boolean;
}

export interface SaveData {
  gold: number;
  shop: Record<string, number>;
  cleared: number;
  best: Record<string, number>;
  character: string;
  stage: number;
  stormTier: number;
  kills: number;
  mainXp: number; // main (account) level xp, see rules.mainLevel. Was `xp`: renaming it reset every player's main level once
  gear: GearState;
  bestiary: Record<string, number>;
  bounties: Record<string, number>;
  evolutions: Record<string, boolean>;
  mastery: Record<string, number>;
  dailyClaimed: string;
  settings: Settings;
}

const KEY = 'mana-storm-save-v1';

export function defaultsForTest(): SaveData {
  return defaults();
}

export function defaults(): SaveData {
  return {
    gold: 0,
    shop: {},
    cleared: 0,
    best: {},
    character: 'aria',
    stage: 0,
    stormTier: 0,
    kills: 0,
    mainXp: 0,
    gear: emptyGear(),
    bestiary: {},
    bounties: {},
    evolutions: {},
    mastery: {},
    dailyClaimed: '',
    // Auto graphics by default: it starts phones a step down and lowers the tier itself when frames are slow.
    settings: { graphicsMode: 'auto', sfx: 0.7, music: 0.45, shake: true, numbers: true, lang: 'fa', joystick: 'floating', leftHanded: false, haptics: true },
  };
}

const num = (v: unknown, fallback: number, min: number, max: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;

// localStorage is user-editable: clamp every field instead of trusting it.
export function loadSave(): SaveData {
  const data = defaults();
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<SaveData> | null;
    if (!raw || typeof raw !== 'object') return data;
    data.gold = Math.floor(num(raw.gold, 0, 0, 1e9));
    data.cleared = Math.floor(num(raw.cleared, 0, 0, STAGES.length));
    data.kills = Math.floor(num(raw.kills, 0, 0, 1e12));
    data.mainXp = Math.floor(num(raw.mainXp, 0, 0, 1e9));
    data.stage = Math.floor(num(raw.stage, 0, 0, STAGES.length - 1));
    data.stormTier = Math.floor(num(raw.stormTier, 0, 0, MAX_STORM_TIER));
    if (typeof raw.character === 'string') data.character = raw.character;
    data.gear = cleanGear(raw.gear);
    if (raw.bestiary && typeof raw.bestiary === 'object') {
      for (const [k, v] of Object.entries(raw.bestiary)) data.bestiary[k] = Math.floor(num(v, 0, 0, 1e9));
    }
    if (raw.bounties && typeof raw.bounties === 'object') {
      for (const [k, v] of Object.entries(raw.bounties)) data.bounties[k] = Math.floor(num(v, 0, 0, 3));
    }
    if (raw.evolutions && typeof raw.evolutions === 'object') {
      for (const [k, v] of Object.entries(raw.evolutions)) if (v === true) data.evolutions[k] = true;
    }
    if (raw.mastery && typeof raw.mastery === 'object') {
      for (const [k, v] of Object.entries(raw.mastery)) data.mastery[k] = Math.floor(num(v, 0, 0, 1e9));
    }
    if (typeof raw.dailyClaimed === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.dailyClaimed)) {
      data.dailyClaimed = raw.dailyClaimed;
    }
    for (const item of SHOP) data.shop[item.id] = Math.floor(num(raw.shop?.[item.id], 0, 0, item.maxRank));
    for (const stage of STAGES) {
      const best = raw.best?.[stage.id];
      if (best !== undefined) data.best[stage.id] = num(best, 0, 0, 1e6);
    }
    const s = raw.settings;
    if (s) {
      if (s.graphicsMode === 'auto' || s.graphicsMode === 'low' || s.graphicsMode === 'medium' || s.graphicsMode === 'high') data.settings.graphicsMode = s.graphicsMode;
      data.settings.sfx = num(s.sfx, 0.7, 0, 1);
      data.settings.music = num(s.music, 0.45, 0, 1);
      data.settings.shake = s.shake !== false;
      data.settings.numbers = s.numbers !== false;
      if (s.lang === 'en' || s.lang === 'fa') data.settings.lang = s.lang;
      if (s.joystick === 'floating' || s.joystick === 'fixed') data.settings.joystick = s.joystick;
      data.settings.leftHanded = s.leftHanded === true;
      data.settings.haptics = s.haptics !== false;
    }
  } catch {
    // Private mode or corrupt data: play with defaults.
  }
  return data;
}

export function writeSave(data: SaveData): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable; progress lasts for this session only.
  }
}
